import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Scale, ShieldCheck, FileText, DollarSign, 
  Download, Lock, CheckCircle2, Clock, ArrowRight,
  ChevronDown, Star, Users, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandingPage = () => {
  const navigate = useNavigate();
  const [depositCount, setDepositCount] = useState(0);

  useEffect(() => {
    // Animate counter
    const target = 847500;
    const duration = 2000;
    const increment = target / (duration / 16);
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDepositCount(target);
        clearInterval(timer);
      } else {
        setDepositCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, []);

  const formatMoney = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const features = [
    {
      icon: Scale,
      title: "Legally Compliant",
      description: "Our letters cite your state's specific security deposit laws and statutes."
    },
    {
      icon: Clock,
      title: "Ready in Minutes",
      description: "Answer a few questions, get a professional demand letter instantly."
    },
    {
      icon: ShieldCheck,
      title: "All 50 States",
      description: "Templates customized for every state's unique deposit return requirements."
    },
    {
      icon: Zap,
      title: "Proven Results",
      description: "Landlords take formal demand letters seriously. Most respond within days."
    }
  ];

  const testimonials = [
    {
      quote: "My landlord ignored my texts for weeks. After sending this letter, I got my full $1,800 deposit back within 10 days.",
      author: "Sarah M.",
      location: "California",
      amount: "$1,800"
    },
    {
      quote: "Worth every penny. The letter was so professional, my landlord called to apologize and sent my deposit the next day.",
      author: "Marcus T.",
      location: "Texas",
      amount: "$2,400"
    },
    {
      quote: "I was ready to give up on my deposit. This letter got results when nothing else worked.",
      author: "Jennifer K.",
      location: "New York",
      amount: "$3,200"
    }
  ];

  const faqs = [
    {
      question: "Is this letter legally binding?",
      answer: "Yes. The letter cites your state's specific security deposit laws and serves as formal legal notice. If your landlord doesn't comply, you can use this letter as evidence in small claims court."
    },
    {
      question: "How long does it take to create my letter?",
      answer: "Most users complete the form in under 5 minutes. Your customized letter is generated instantly and ready for download immediately after payment."
    },
    {
      question: "What if my landlord still doesn't return my deposit?",
      answer: "Your letter establishes a clear paper trail for legal action. Most landlords respond to avoid court. If they don't, the letter strengthens your small claims case significantly."
    },
    {
      question: "Do you offer refunds?",
      answer: "Due to the immediate delivery of the digital product, we cannot offer refunds. However, we stand behind our letters – they're crafted by legal professionals and comply with all state requirements."
    },
    {
      question: "Should I send this letter certified mail?",
      answer: "Absolutely. We recommend sending via Certified Mail with Return Receipt Requested. This creates proof of delivery that's valuable if you need to go to court."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-8 w-8 text-blue-600" />
              <span className="font-serif text-xl font-bold text-slate-900">TenantShield</span>
            </div>
            <Button 
              data-testid="nav-get-started-btn"
              onClick={() => navigate('/create')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Get Your Letter
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-24 pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="hero-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="h-4 w-4 fill-blue-600" />
                Trusted by 10,000+ tenants
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Get Your Security Deposit{" "}
                <span className="text-blue-600">Back</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                Generate a legally-compliant demand letter in minutes. 
                State-specific templates that get landlords to pay attention.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  data-testid="hero-get-started-btn"
                  onClick={() => navigate('/create')}
                  className="btn-cta text-lg px-8 py-6"
                >
                  Create Your Letter <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <div className="flex items-center gap-2 text-slate-600">
                  <Lock className="h-4 w-4" />
                  <span className="text-sm">Only $29 • Instant Download</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="trust-badge">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  All 50 States
                </div>
                <div className="trust-badge">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Instant Delivery
                </div>
                <div className="trust-badge">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Legal Templates
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative rounded-none shadow-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1706200234277-3586cd003ba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHN1bm55fGVufDB8fHx8MTc3MjI3MDEwMnww&ixlib=rb-4.1.0&q=85"
                  alt="Modern apartment"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <p className="text-sm font-medium mb-1">Deposits recovered</p>
                  <p className="stat-number text-3xl">{formatMoney(depositCount)}+</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why TenantShield Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our demand letters are designed to get results. Here's what makes them effective.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <feature.icon className="h-10 w-10 text-blue-600 mb-4" />
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Three Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, title: "Fill Out the Form", desc: "Enter your details and select your state. Takes under 5 minutes." },
              { step: 2, title: "Preview Your Letter", desc: "Review your customized demand letter with state-specific legal citations." },
              { step: 3, title: "Download & Send", desc: "Pay $29, download your PDF, and send via certified mail." }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 text-white font-serif text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Real Results from Real Tenants
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <p className="text-slate-700 mb-6 relative z-10">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Recovered</p>
                    <p className="font-mono font-bold text-green-600">{testimonial.amount}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-16 lg:py-24 subtle-glow">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-white border border-slate-200 shadow-xl p-8 md:p-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Get Your Deposit Back
            </h2>
            <p className="text-slate-600 mb-6">
              One flat fee. No hidden costs. No subscription.
            </p>
            
            <div className="mb-6">
              <span className="price-tag">$29</span>
              <span className="text-slate-500 ml-2">one-time payment</span>
            </div>

            <ul className="text-left max-w-sm mx-auto mb-8 space-y-3">
              {[
                "Customized for your state's laws",
                "Professional legal formatting",
                "Instant PDF download",
                "Email delivery included",
                "Print-ready for certified mail"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <Button 
              data-testid="pricing-get-started-btn"
              onClick={() => navigate('/create')}
              className="btn-cta w-full sm:w-auto"
            >
              Create Your Letter Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-slate-200 px-6 data-[state=open]:bg-slate-50"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-blue-400" />
              <span className="font-serif text-lg font-bold">TenantShield</span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2026 TenantShield. All rights reserved. Not a law firm.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
